import React, { useState, useEffect } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import categoryService from '../services/categoryService';
import toast from 'react-hot-toast';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    color: '#6366F1',
    keywords: '',
    icon: 'tag'
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await categoryService.getAllCategories();
      if (response.success) {
        setCategories(response.data);
      } else {
        toast.error('Failed to load categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    try {
      setLoading(true);
      let response;
      
      if (editingCategory) {
        response = await categoryService.updateCategory(editingCategory._id, {
          ...newCategory,
          keywords: newCategory.keywords.split(',').map(k => k.trim()),
        });
        toast.success(`Category "${newCategory.name}" updated`);
      } else {
        response = await categoryService.createCategory({
          ...newCategory,
          keywords: newCategory.keywords.split(',').map(k => k.trim()),
        });
        toast.success(`Category "${newCategory.name}" created`);
      }
      
      if (response.success) {
        resetForm();
        fetchCategories();
      } else {
        toast.error(response.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    if (category.isSystem) {
      toast.error('System categories cannot be edited');
      return;
    }
    
    setEditingCategory(category);
    setNewCategory({
      name: category.name,
      color: category.color,
      keywords: category.keywords?.join(', ') || '',
      icon: category.icon || 'tag'
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id, name, isSystem) => {
    if (isSystem) {
      toast.error('System categories cannot be deleted');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete the category "${name}"?`)) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await categoryService.deleteCategory(id);
      
      if (response.success) {
        toast.success(`Category "${name}" deleted`);
        fetchCategories();
      } else {
        toast.error(response.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewCategory({ name: '', color: '#6366F1', keywords: '', icon: 'tag' });
    setEditingCategory(null);
    setShowAddForm(false);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-indigo-600">Category Management</h1>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center shadow-sm transition-all duration-200 transform hover:scale-105"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Category
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="bg-white/80 backdrop-blur-sm shadow-md rounded-lg p-6 mb-6 border border-indigo-100">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-indigo-700">
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </h2>
            <button 
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                  className="w-full rounded-md bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Category name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <div className="flex items-center">
                  <input
                    type="color"
                    value={newCategory.color}
                    onChange={(e) => setNewCategory({...newCategory, color: e.target.value})}
                    className="h-10 w-10 rounded border-0 shadow-sm mr-2"
                  />
                  <input
                    type="text"
                    value={newCategory.color}
                    onChange={(e) => setNewCategory({...newCategory, color: e.target.value})}
                    className="flex-1 rounded-md bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="#RRGGBB"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                <input
                  type="text"
                  value={newCategory.icon}
                  onChange={(e) => setNewCategory({...newCategory, icon: e.target.value})}
                  className="w-full rounded-md bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Icon name (e.g., tag, shopping-cart)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Keywords (comma separated)</label>
                <input
                  type="text"
                  value={newCategory.keywords}
                  onChange={(e) => setNewCategory({...newCategory, keywords: e.target.value})}
                  className="w-full rounded-md bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., grocery, food, market"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 shadow-sm transition-all duration-200"
              >
                {loading ? 'Saving...' : (editingCategory ? 'Update Category' : 'Create Category')}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading && !categories.length ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading categories...</p>
        </div>
      ) : (
        <div className="bg-white/80 backdrop-blur-sm shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-indigo-50/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-500 uppercase tracking-wider">Color</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-500 uppercase tracking-wider">Keywords</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-indigo-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-indigo-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No categories found
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr key={category._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span 
                        className="inline-block w-6 h-6 rounded-full shadow-sm border border-gray-200"
                        style={{ backgroundColor: category.color }}
                        title={category.color}
                      ></span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{category.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {category.keywords?.length > 0 ? (
                          category.keywords.slice(0, 5).map((keyword, index) => (
                            <span 
                              key={index}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
                            >
                              {keyword}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">No keywords</span>
                        )}
                        {category.keywords?.length > 5 && (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                            +{category.keywords.length - 5} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-1.5 inline-flex text-xs leading-4 font-medium rounded-full ${
                        category.isSystem 
                          ? 'bg-indigo-100 text-indigo-800 border border-indigo-200' 
                          : 'bg-green-100 text-green-800 border border-green-200'
                      }`}>
                        {category.isSystem ? 'System' : 'Custom'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(category)}
                          className={`p-1.5 rounded-full ${category.isSystem 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200 hover:text-indigo-800'}`}
                          disabled={loading || category.isSystem}
                          title={category.isSystem ? "System categories cannot be edited" : "Edit category"}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(category._id, category.name, category.isSystem)}
                          className={`p-1.5 rounded-full ${category.isSystem 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800'}`}
                          disabled={loading || category.isSystem}
                          title={category.isSystem ? "System categories cannot be deleted" : "Delete category"}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement; 